import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { TuiIcon, TUI_DARK_MODE } from '@taiga-ui/core';

@Component({
  selector: 'app-time-indicator',
  standalone: true,
  imports: [TuiIcon],
  templateUrl: './time-indicator.component.html',
  styleUrl: './time-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeIndicatorComponent {
  private static readonly THEME_KEY = 'rbi-theme';

  private readonly destroyRef = inject(DestroyRef);
  /** Señal de Taiga: al escribirla, el tema oscuro se aplica en toda la app. */
  private readonly darkMode = inject(TUI_DARK_MODE);

  readonly currentTime = signal(new Date());
  readonly isDark = this.darkMode;

  readonly timeBadge = computed(() => {
    const hour = this.currentTime().getHours();
    const label = hour < 12 ? 'AM' : 'PM';
    const dark = this.isDark();

    return {
      label,
      icon: dark ? '@tui.moon' : '@tui.sun',
      className: dark
        ? 'time-indicator--night'
        : hour < 18
          ? 'time-indicator--morning'
          : 'time-indicator--afternoon',
    };
  });

  constructor() {
    this.restoreStoredTheme();

    interval(60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentTime.set(new Date());
      });
  }

  toggleTheme(): void {
    const next = !this.darkMode();
    this.darkMode.set(next);

    try {
      localStorage.setItem(TimeIndicatorComponent.THEME_KEY, next ? 'dark' : 'light');
    } catch {
      /* storage not available */
    }
  }

  private restoreStoredTheme(): void {
    try {
      const stored = localStorage.getItem(TimeIndicatorComponent.THEME_KEY);
      if (stored === 'dark' || stored === 'light') {
        this.darkMode.set(stored === 'dark');
      }
    } catch {
      /* storage not available */
    }
  }
}
